import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { getTotpStatus, totpSetup, totpVerify } from "../utils/peticionesApi";
import { ShieldX, Smartphone, Lock } from "lucide-react";
import "../css/TotpModal.css";

const STEPS = { LOADING: "loading", NO_ACCESS: "no_access", SETUP_QR: "setup_qr", SETUP_VERIFY: "setup_verify", VERIFY: "verify" };

const MODULE_LABELS = {
    RECIBOS: "Recibos",
    FACTURACION: "Facturación",
    CONTABILIDAD: "Contabilidad"
};

const TotpGate = ({ module, children }) => {
    const [step, setStep] = useState(STEPS.LOADING);
    const [qrData, setQrData] = useState(null);
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    const moduleLabel = MODULE_LABELS[module] || module;

    const checkStatus = useCallback(async () => {
        // Si ya tiene session token válido, pasar directo
        const existingSession = sessionStorage.getItem("totp_session");
        if (existingSession) {
            setStep(null);
            return;
        }

        try {
            const res = await getTotpStatus(module);
            const { has_access, totp_configured } = res.data;

            if (!has_access) {
                setStep(STEPS.NO_ACCESS);
            } else if (!totp_configured) {
                setStep(STEPS.SETUP_QR);
            } else {
                setStep(STEPS.VERIFY);
            }
        } catch (err) {
            console.error("Error checking TOTP status:", err);
            setStep(STEPS.NO_ACCESS);
        }
    }, [module]);

    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    useEffect(() => {
        if ((step === STEPS.SETUP_VERIFY || step === STEPS.VERIFY) && inputRef.current) {
            inputRef.current.focus();
        }
    }, [step]);

    const handleSetup = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await totpSetup();
            setQrData(res.data);
            setStep(STEPS.SETUP_VERIFY);
        } catch (err) {
            setError("Error al generar el código QR. Intentá de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async () => {
        if (code.length !== 6) return;
        setLoading(true);
        setError("");
        try {
            const res = await totpVerify(code);
            if (res.data.valid) {
                sessionStorage.setItem("totp_session", res.data.session_token);
                setStep(null);
            } else {
                setError("Código incorrecto. Verificá en Google Authenticator.");
                setCode("");
                inputRef.current?.focus();
            }
        } catch (err) {
            setError("Error al verificar el código.");
        } finally {
            setLoading(false);
        }
    };

    const handleCodeChange = (e) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 6);
        setCode(val);
        setError("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && code.length === 6) {
            handleVerify();
        }
    };

    // Pasó la verificación, renderizar children
    if (step === null) return children;

    // Loading inicial
    if (step === STEPS.LOADING) return null;

    const modal = (
        <div className="totp-overlay">
            <div className="totp-modal">
                {/* NO ACCESS */}
                {step === STEPS.NO_ACCESS && (
                    <>
                        <div className="totp-icon denied">
                            <ShieldX />
                        </div>
                        <h2>Acceso restringido</h2>
                        <p>No tenés permisos para acceder a {moduleLabel}. Contactá al administrador del sistema.</p>
                        <div className="totp-actions">
                            <button className="totp-btn-secondary" onClick={() => window.history.back()}>
                                Volver
                            </button>
                        </div>
                    </>
                )}

                {/* SETUP - Mostrar QR */}
                {step === STEPS.SETUP_QR && (
                    <>
                        <div className="totp-step-indicator">
                            <div className="totp-step active" />
                            <div className="totp-step" />
                        </div>
                        <div className="totp-icon">
                            <Smartphone />
                        </div>
                        <h2>Configurar autenticación</h2>
                        <p>
                            Para acceder a {moduleLabel} necesitás configurar Google Authenticator.
                            Hacé click en "Generar QR" y escanealo con la app.
                        </p>
                        <div className="totp-actions">
                            <button className="totp-btn-secondary" onClick={() => window.history.back()}>
                                Cancelar
                            </button>
                            <button className="totp-btn-primary" onClick={handleSetup} disabled={loading}>
                                {loading && <span className="totp-spinner" />}
                                Generar QR
                            </button>
                        </div>
                    </>
                )}

                {/* SETUP - Verificar primer código */}
                {step === STEPS.SETUP_VERIFY && qrData && (
                    <>
                        <div className="totp-step-indicator">
                            <div className="totp-step" />
                            <div className="totp-step active" />
                        </div>
                        <h2>Escaneá el código QR</h2>
                        <p>Abrí Google Authenticator y escaneá este código. Luego ingresá el código de 6 dígitos que aparece.</p>

                        <div className="totp-qr-container">
                            <img
                                src={`data:image/png;base64,${qrData.qr_code_base64}`}
                                alt="QR TOTP"
                            />
                        </div>

                        <div className="totp-secret-box">
                            <small>Clave manual (si no podés escanear):</small>
                            {qrData.secret}
                        </div>

                        <div className="totp-input-group">
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="000000"
                                value={code}
                                onChange={handleCodeChange}
                                onKeyDown={handleKeyDown}
                                className={error ? "error" : ""}
                                maxLength={6}
                            />
                        </div>
                        {error && <div className="totp-error">{error}</div>}

                        <div className="totp-actions">
                            <button className="totp-btn-secondary" onClick={() => { setStep(STEPS.SETUP_QR); setCode(""); setError(""); }}>
                                Atrás
                            </button>
                            <button className="totp-btn-primary" onClick={handleVerify} disabled={code.length !== 6 || loading}>
                                {loading && <span className="totp-spinner" />}
                                Verificar
                            </button>
                        </div>
                    </>
                )}

                {/* VERIFY - Usuario ya configurado, pedir código */}
                {step === STEPS.VERIFY && (
                    <>
                        <div className="totp-icon">
                            <Lock />
                        </div>
                        <h2>Verificación requerida</h2>
                        <p>Ingresá el código de 6 dígitos de Google Authenticator para acceder a {moduleLabel}.</p>

                        <div className="totp-input-group">
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="000000"
                                value={code}
                                onChange={handleCodeChange}
                                onKeyDown={handleKeyDown}
                                className={error ? "error" : ""}
                                maxLength={6}
                            />
                        </div>
                        {error && <div className="totp-error">{error}</div>}

                        <div className="totp-actions">
                            <button className="totp-btn-secondary" onClick={() => window.history.back()}>
                                Cancelar
                            </button>
                            <button className="totp-btn-primary" onClick={handleVerify} disabled={code.length !== 6 || loading}>
                                {loading && <span className="totp-spinner" />}
                                Acceder
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default TotpGate;
